import { useRef } from 'react';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import { useAnalysisFormStore, type AnalysisFormState } from '../analysisFormStore';

/** Derives a step's "can continue" flag from the analysis form store state. */
export type AnalysisStepValidityPredicate = (state: AnalysisFormState) => boolean;

/**
 * Report a wizard step's validity to its parent (`StepperPageTemplate`) by
 * subscribing to the analysis form store: once on mount with the initial
 * computed validity, then only when the computed boolean actually flips.
 *
 * Only the predicate should differ between steps, which is why that notify
 * semantic lives here rather than in each one. Guarding on the computed value
 * rather than on a slice identity is both stricter and predicate-agnostic: an
 * unrelated store write (or a new array identity holding the same selection)
 * cannot produce a redundant call, and a predicate reading several slices still
 * cannot miss a real flip.
 *
 * Mechanism: `useMountEffect` (no-use-effect skill, Rule 4 — the sanctioned
 * escape hatch for mount-time external-system subscriptions), which the skill
 * allows only inside a reusable hook like this one. `predicate` and
 * `onValidChange` are read through refs so the subscription is established
 * exactly once and never needs a dependency array; that is what
 * `useMountEffect`'s contract prescribes for latest-value access, not a bandage
 * over a re-running effect.
 *
 * `useSyncExternalStore` is deliberately NOT used: this hook produces no render
 * value, it pushes a notification to a parent, so rendering from a store
 * snapshot would still leave that notification to arrange.
 */
export function useAnalysisStepValidity(
  predicate: AnalysisStepValidityPredicate,
  onValidChange?: (isValid: boolean) => void,
): void {
  const predicateRef = useRef(predicate);
  predicateRef.current = predicate;
  const onValidChangeRef = useRef(onValidChange);
  onValidChangeRef.current = onValidChange;

  useMountEffect(() => {
    let lastValid = predicateRef.current(useAnalysisFormStore.getState());
    onValidChangeRef.current?.(lastValid);

    return useAnalysisFormStore.subscribe((state) => {
      const nextValid = predicateRef.current(state);
      if (nextValid === lastValid) return;
      lastValid = nextValid;
      onValidChangeRef.current?.(nextValid);
    });
  });
}
